import React from 'react';
import { Box, Button, Chip, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';

import { FormattedNumberTextField } from '../../common/FormattedNumberTextField';
import { LLMModelUnitPriceInput, LLMModelUnitPriceType } from '../../../types/admin';
import { usePricingDimensions } from '../../../services/llm-catalog.service';
import {
  RESOLUTION_DIMENSION_KEY,
  suggestedUnitPriceType,
  UNIT_PRICE_OPTIONS,
} from './modelPricing';
import {
  canonicalCondition,
  findDuplicateUnitPriceIdentities,
  unitPriceIdentity,
} from './unitPriceIdentity';

export interface ModelUnitPricingSectionProps {
  value: LLMModelUnitPriceInput[];
  taskTypeCode?: string;
  modelPublicId?: string;
  onChange: (next: LLMModelUnitPriceInput[]) => void;
}

export const ModelUnitPricingSection: React.FC<ModelUnitPricingSectionProps> = ({
  value,
  taskTypeCode,
  modelPublicId,
  onChange,
}) => {
  const { data: allDimensions = [] } = usePricingDimensions(modelPublicId);
  // Resolution has its own section (ModelResolutionPricingSection).
  const dimensions = allDimensions.filter(
    (dimension) => dimension.key !== RESOLUTION_DIMENSION_KEY
  );
  const suggestedType = value.length === 0 ? suggestedUnitPriceType(taskTypeCode) : null;
  const suggestedOption = UNIT_PRICE_OPTIONS.find((option) => option.unitType === suggestedType);
  const [dimensionKey, setDimensionKey] = React.useState('');
  const [dimensionUnit, setDimensionUnit] = React.useState<LLMModelUnitPriceType>(
    suggestedUnitPriceType(taskTypeCode) ?? 'video_second'
  );
  const selectedDimension = dimensions.find((dimension) => dimension.key === dimensionKey);
  const duplicateErrors = findDuplicateUnitPriceIdentities(value);

  const updateRow = (index: number, patch: Partial<LLMModelUnitPriceInput>) => {
    onChange(value.map((row, itemIndex) => (itemIndex === index ? { ...row, ...patch } : row)));
  };

  const addUnitPrice = (unitType?: LLMModelUnitPriceType) => {
    const nextType =
      unitType ??
      UNIT_PRICE_OPTIONS.find((option) => !value.some((row) => row.unitType === option.unitType))
        ?.unitType;

    if (!nextType) return;

    onChange([...value, { unitType: nextType, priceUsd: null, unitScale: 1 }]);
  };

  const addRowsForDimension = () => {
    if (!selectedDimension) return;
    const existing = new Set(value.map(unitPriceIdentity));
    const rows = selectedDimension.options.flatMap((option) => {
      const next = {
        unitType: dimensionUnit,
        priceUsd: null,
        unitScale: 1,
        condition: { [selectedDimension.key]: option },
      };
      return existing.has(unitPriceIdentity(next)) ? [] : [next];
    });
    if (rows.length > 0) onChange([...value, ...rows]);
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_row, itemIndex) => itemIndex !== index));
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        className="mb-1 font-semibold text-gray-700 dark:text-slate-300"
      >
        Unit Pricing
      </Typography>
      <Typography variant="caption" className="mb-3 block text-gray-500 dark:text-slate-400">
        For models billed per audio minute, characters, image or request. Used by the gateway to
        convert real usage into tokens.
      </Typography>

      <Box className="space-y-3">
        {value.map((row, index) => {
          const usedTypes = new Set(
            value
              .filter((_item, itemIndex) => itemIndex !== index)
              .filter(
                (item) => canonicalCondition(item.condition) === canonicalCondition(row.condition)
              )
              .map((item) => item.unitType)
          );
          const conditionWarning = row.condition
            ? conditionWarningLabel(row.condition, dimensions)
            : null;

          return (
            <Box
              key={`${row.unitType}-${index}`}
              className="grid grid-cols-1 items-start gap-3 rounded border border-slate-200 p-3 dark:border-slate-700 md:grid-cols-[2fr_2fr_1fr_auto]"
            >
              <TextField
                select
                fullWidth
                label="Unit"
                value={row.unitType}
                onChange={(event) =>
                  updateRow(index, { unitType: event.target.value as LLMModelUnitPriceType })
                }
              >
                {UNIT_PRICE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.unitType}
                    value={option.unitType}
                    disabled={usedTypes.has(option.unitType)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {row.condition && (
                <Box className="flex flex-wrap gap-1">
                  {Object.entries(row.condition).map(([key, item]) => (
                    <Chip key={key} size="small" label={`${key} = ${String(item)}`} />
                  ))}
                  {conditionWarning && (
                    <Chip size="small" color="warning" label="not in current format" />
                  )}
                </Box>
              )}

              <FormattedNumberTextField
                fullWidth
                label="Price (USD)"
                value={row.priceUsd}
                integer={false}
                allowEmpty
                onValueChange={(priceUsd) => updateRow(index, { priceUsd: priceUsd ?? null })}
                inputProps={{ min: 0, step: 0.00000001 }}
              />

              <FormattedNumberTextField
                fullWidth
                label="Per N units"
                value={row.unitScale || 1}
                allowEmpty
                onValueChange={(unitScale) =>
                  updateRow(index, { unitScale: Math.max(1, unitScale ?? 1) })
                }
                inputProps={{ min: 1, step: 1 }}
              />

              <IconButton
                className="justify-self-start md:mt-2"
                aria-label="Delete unit price"
                onClick={() => removeRow(index)}
              >
                <Trash2 size={18} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {duplicateErrors.length > 0 && (
        <Typography variant="caption" className="mt-2 block text-red-600">
          Duplicate unit price rows: {duplicateErrors.join(', ')}
        </Typography>
      )}

      <Box className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outlined"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => addUnitPrice()}
        >
          Add unit price
        </Button>
        {suggestedOption && (
          <Button size="small" onClick={() => addUnitPrice(suggestedOption.unitType)}>
            Add {suggestedOption.label}
          </Button>
        )}
      </Box>

      {dimensions.length > 0 && (
        <Box className="mt-4 grid grid-cols-1 items-center gap-3 rounded border border-slate-200 p-3 dark:border-slate-700 md:grid-cols-[2fr_2fr_auto]">
          <TextField
            select
            size="small"
            label="Price by field"
            value={dimensionKey}
            onChange={(event) => setDimensionKey(event.target.value)}
          >
            {dimensions.map((dimension) => (
              <MenuItem key={dimension.key} value={dimension.key}>
                {dimension.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Unit"
            value={dimensionUnit}
            onChange={(event) => setDimensionUnit(event.target.value as LLMModelUnitPriceType)}
          >
            {UNIT_PRICE_OPTIONS.map((option) => (
              <MenuItem key={option.unitType} value={option.unitType}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            size="small"
            disabled={!selectedDimension}
            onClick={addRowsForDimension}
          >
            Add rows
          </Button>
        </Box>
      )}
    </Box>
  );
};

function conditionWarningLabel(
  condition: Record<string, string | number | boolean>,
  dimensions: Array<{ key: string; options: Array<string | number | boolean> }>
): string | null {
  for (const [key, value] of Object.entries(condition)) {
    const dimension = dimensions.find((item) => item.key === key);
    if (!dimension) return key;
    if (!dimension.options.some((option) => option === value)) return key;
  }
  return null;
}
